import { useState, useEffect } from "react"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import CenteredModal from "@/src/common/centered-modal/CenteredModal"
import { Iconify } from "@/src/common/iconify"
import { useAuthStore } from "@/src/store"
import { editUser, type InternalWorkspaceUserApi } from "@/src/services/internal"
import {
    WORKSPACE_USER_ROLE_OPTIONS,
    DEFAULT_WORKSPACE_ROLE,
    getEditableWorkspaceRole,
    ROLES,
    type WorkspaceRoleValue,
} from "@/src/features/internalUsers/constants"

// ----------------------------------------------------------------------

export interface EditUserModalProps {
    open: boolean
    onClose: () => void
    user: InternalWorkspaceUserApi | null
    workspaceId: string
    onSuccess?: () => void
}

export function EditUserModal({ open, onClose, user, workspaceId, onSuccess }: EditUserModalProps) {
    const currentUser = useAuthStore((state) => state.user)
    const isLoggedInUserInternal = Boolean(
        currentUser?.workspaces?.some((w) => String(w.roleName ?? "").toUpperCase() === ROLES.INTERNAL)
    )
    const isEditingSelf = Boolean(user && currentUser?.id === user.userId)
    const isRoleDisabled = isLoggedInUserInternal && isEditingSelf

    const [selectedRole, setSelectedRole] = useState<WorkspaceRoleValue>(
        user?.role ? getEditableWorkspaceRole(user.role) : DEFAULT_WORKSPACE_ROLE
    )
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [passwordError, setPasswordError] = useState("")
    const [submitError, setSubmitError] = useState("")

    useEffect(() => {
        if (user) {
            setSelectedRole(getEditableWorkspaceRole(user.role))
            setNewPassword("")
            setConfirmPassword("")
            setPasswordError("")
            setSubmitError("")
        }
    }, [user, open])

    const handleClose = () => {
        setNewPassword("")
        setConfirmPassword("")
        setPasswordError("")
        setSubmitError("")
        onClose()
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!user) return
        setPasswordError("")
        if (newPassword.trim() && newPassword !== confirmPassword) {
            setPasswordError("Passwords do not match")
            return
        }
        setSubmitting(true)
        setSubmitError("")
        try {
            const payload = {
                role: selectedRole,
                ...(newPassword.trim() && { password: newPassword.trim() }),
            }
            await editUser(workspaceId, user.userId, payload)
            onSuccess?.()
            handleClose()
        } catch {
            setSubmitError("Failed to update user. Please try again.")
        } finally {
            setSubmitting(false)
        }
    }

    if (!user) return null

    return (
        <CenteredModal open={open} onClose={handleClose} width={440}>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    "& > *": { flexShrink: 0 },
                }}
            >
                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                bgcolor: "primary.lighter",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Iconify icon="solar:pen-bold" width={24} sx={{ color: "primary.main" }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600}>
                            Edit User
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={handleClose} aria-label="Close">
                        <Iconify icon="mingcute:close-line" width={24} />
                    </IconButton>
                </Stack>

                <Stack spacing={3.5}>
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                            {user.fullName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {user.email}
                        </Typography>
                    </Box>

                    <TextField
                        fullWidth
                        select
                        label="Role"
                        value={selectedRole}
                        onChange={(event) => setSelectedRole(event.target.value as WorkspaceRoleValue)}
                        size="small"
                        disabled={isRoleDisabled}
                    >
                        {WORKSPACE_USER_ROLE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        label="New Password"
                        placeholder="Enter new password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Iconify icon="mdi:key-outline" width={20} sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Confirm Password"
                        placeholder="Confirm new password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => {
                            setConfirmPassword(e.target.value)
                            setPasswordError("")
                        }}
                        size="small"
                        error={Boolean(passwordError)}
                        helperText={passwordError}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Iconify icon="mdi:key-outline" width={20} sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Typography variant="caption" color="text.secondary">
                        Leave password fields empty to keep the current password.
                    </Typography>

                    {submitError && (
                        <Typography variant="body2" color="error">
                            {submitError}
                        </Typography>
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={submitting}
                        sx={{ mt: 1 }}
                    >
                        {submitting ? "Saving..." : "Save Changes"}
                    </Button>
                </Stack>
            </Box>
        </CenteredModal>
    )
}
